import React, { useState, Component } from 'react';
import { LayoutDashboard, Tag, UtensilsCrossed, CheckCircle2, AlertCircle, Printer, Filter, Search, Plus, Edit, Trash2, Calendar, ChevronRight, Info, HelpCircle, FileText, Clock, ArrowRight, Menu, X, ChevronDown, ChevronUp } from 'lucide-react';
import { useDocumentTitle } from '../utils/useDocumentTitle';
// --- Components ---
function Section({
  title,
  children,
  id
}: {
  title: string;
  children: React.ReactNode;
  id?: string;
}) {
  return <div id={id} className="mb-12 scroll-mt-24" data-id="element-1300">
      <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-1301">
        {title}
      </h2>
      <div className="space-y-4 text-gray-600 leading-relaxed" data-id="element-1302">{children}</div>
    </div>;
}
function SubSection({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <div className="mb-6" data-id="element-1303">
      <h3 className="text-lg font-semibold text-gray-800 mb-3" data-id="element-1304">{title}</h3>
      <div className="space-y-3 text-gray-600" data-id="element-1305">{children}</div>
    </div>;
}
function Callout({
  type = 'info',
  title,
  children
}: {
  type?: 'info' | 'warning' | 'success' | 'danger';
  title?: string;
  children: React.ReactNode;
}) {
  const styles = {
    info: 'bg-blue-50 border-blue-100 text-blue-800',
    warning: 'bg-amber-50 border-amber-100 text-amber-800',
    success: 'bg-green-50 border-green-100 text-green-800',
    danger: 'bg-red-50 border-red-100 text-red-800'
  };
  const icons = {
    info: Info,
    warning: AlertCircle,
    success: CheckCircle2,
    danger: AlertCircle
  };
  const Icon = icons[type];
  return <div className={`p-4 rounded-xl border ${styles[type]} my-4`} data-id="element-1306">
      <div className="flex gap-3" data-id="element-1307">
        <Icon className="w-5 h-5 shrink-0 mt-0.5" data-id="element-1308" />
        <div className="flex-1" data-id="element-1309">
          {title && <h4 className="font-semibold mb-1" data-id="element-1310">{title}</h4>}
          <div className="text-sm opacity-90" data-id="element-1311">{children}</div>
        </div>
      </div>
    </div>;
}
function StepList({
  steps
}: {
  steps: string[];
}) {
  return <div className="space-y-3 my-4" data-id="element-1312">
      {steps.map((step, i) => <div key={i} className="flex gap-3" data-id="element-1313">
          <div className="w-6 h-6 rounded-full bg-gray-100 text-gray-600 font-bold text-xs flex items-center justify-center shrink-0 mt-0.5 border border-gray-200" data-id="element-1314">
            {i + 1}
          </div>
          <p className="text-gray-700 text-sm" data-id="element-1315">{step}</p>
        </div>)}
    </div>;
}
function FAQ({
  items
}: {
  items: {
    q: string;
    a: string;
  }[];
}) {
  return <div className="space-y-4 mt-8" data-id="element-1316">
      <h3 className="font-bold text-gray-900 flex items-center gap-2" data-id="element-1317">
        <HelpCircle size={18} data-id="element-1318" />
        Preguntas Frecuentes
      </h3>
      <div className="grid gap-3" data-id="element-1319">
        {items.map((item, i) => <details key={i} className="group bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-1320">
            <summary className="flex items-center justify-between p-4 cursor-pointer font-medium text-gray-900 hover:bg-gray-50 transition-colors" data-id="element-1321">
              {item.q}
              <ChevronDown className="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform" data-id="element-1322" />
            </summary>
            <div className="px-4 pb-4 text-sm text-gray-600 bg-gray-50/50 border-t border-gray-100 pt-3" data-id="element-1323">
              {item.a}
            </div>
          </details>)}
      </div>
    </div>;
}
// --- Main Page ---
export function AdminGuidePage() {
  useDocumentTitle('Guía de Uso');
  const [activeTab, setActiveTab] = useState<'orders' | 'labels' | 'menu'>('orders');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const tabs = [{
    id: 'orders',
    label: 'Órdenes',
    icon: LayoutDashboard,
    color: 'text-blue-600 bg-blue-50'
  }, {
    id: 'labels',
    label: 'Etiquetas',
    icon: Tag,
    color: 'text-emerald-600 bg-emerald-50'
  }, {
    id: 'menu',
    label: 'Menú',
    icon: UtensilsCrossed,
    color: 'text-amber-600 bg-amber-50'
  }] as const;
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
      setMobileMenuOpen(false);
    }
  };
  const renderOrdersContent = () => <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-1324">
      <div className="prose prose-gray max-w-none" data-id="element-1325">
        <Section title="Visión General" id="orders-overview" data-id="element-1326">
          <p data-id="element-1327">
            El panel de Órdenes es el centro neurálgico de las operaciones
            diarias. Aquí puedes ver en tiempo real todos los pedidos recibidos,
            gestionar su estado y coordinar la producción y entrega.
          </p>
          <Callout type="info" title="Tip Rápido" data-id="element-1328">
            Mantén esta página abierta durante el servicio para recibir
            notificaciones de nuevos pedidos en tiempo real.
          </Callout>
        </Section>

        <Section title="Flujo de Estados" id="orders-flow" data-id="element-1329">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center my-6 p-4 bg-gray-50 rounded-xl border border-gray-200 overflow-x-auto" data-id="element-1330">
            <div className="flex items-center gap-2 shrink-0" data-id="element-1331">
              <span className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-1332"></span>
              <span className="font-medium text-sm" data-id="element-1333">Pendiente</span>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400 hidden md:block" data-id="element-1334" />
            <div className="flex items-center gap-2 shrink-0" data-id="element-1335">
              <span className="w-3 h-3 rounded-full bg-blue-500" data-id="element-1336"></span>
              <span className="font-medium text-sm" data-id="element-1337">En Preparación</span>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400 hidden md:block" data-id="element-1338" />
            <div className="flex items-center gap-2 shrink-0" data-id="element-1339">
              <span className="w-3 h-3 rounded-full bg-purple-500" data-id="element-1340"></span>
              <span className="font-medium text-sm" data-id="element-1341">Listo</span>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400 hidden md:block" data-id="element-1342" />
            <div className="flex items-center gap-2 shrink-0" data-id="element-1343">
              <span className="w-3 h-3 rounded-full bg-green-500" data-id="element-1344"></span>
              <span className="font-medium text-sm" data-id="element-1345">Entregado</span>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 text-sm" data-id="element-1346">
            <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-100" data-id="element-1347">
              <strong className="text-yellow-800 block mb-1" data-id="element-1348">Pendiente</strong>
              El pedido fue recibido pero aún no ha sido procesado. Los pedidos
              nuevos aparecen aquí automáticamente.
            </div>
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100" data-id="element-1349">
              <strong className="text-blue-800 block mb-1" data-id="element-1350">
                En Preparación
              </strong>
              El pedido está siendo preparado en cocina. Cambia a este estado
              cuando comiences la preparación.
            </div>
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-100" data-id="element-1351">
              <strong className="text-purple-800 block mb-1" data-id="element-1352">Listo</strong>
              El pedido está listo para ser entregado. Úsalo para notificar al
              repartidor.
            </div>
            <div className="p-3 bg-green-50 rounded-lg border border-green-100" data-id="element-1353">
              <strong className="text-green-800 block mb-1" data-id="element-1354">Entregado</strong>
              El pedido fue entregado exitosamente al empleado. Estado final.
            </div>
          </div>
        </Section>

        <Section title="Cómo Cambiar el Estado" id="orders-status" data-id="element-1355">
          <StepList steps={['Localiza el pedido en la tabla o lista de órdenes', 'Haz clic en el botón de estado actual del pedido (aparece como un badge de color)', 'Selecciona el nuevo estado en el menú desplegable', 'El cambio se guarda automáticamente y el empleado es notificado']} data-id="element-1356" />
          <Callout type="warning" data-id="element-1357">
            <strong data-id="element-1358">Importante:</strong> Los estados deben seguir el flujo
            lógico. No saltes de Pendiente a Entregado sin pasar por los estados
            intermedios para mantener un registro preciso.
          </Callout>
        </Section>

        <Section title="Filtros y Búsqueda" id="orders-filters" data-id="element-1359">
          <div className="grid sm:grid-cols-2 gap-4 mb-4" data-id="element-1360">
            <div className="flex gap-3 items-start" data-id="element-1361">
              <Search className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-1362" />
              <div data-id="element-1363">
                <strong className="block text-sm text-gray-900" data-id="element-1364">
                  Búsqueda
                </strong>
                <span className="text-sm text-gray-500" data-id="element-1365">
                  Busca por nombre del empleado, empresa o número de pedido.
                </span>
              </div>
            </div>
            <div className="flex gap-3 items-start" data-id="element-1366">
              <Filter className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-1367" />
              <div data-id="element-1368">
                <strong className="block text-sm text-gray-900" data-id="element-1369">
                  Filtro por Estado
                </strong>
                <span className="text-sm text-gray-500" data-id="element-1370">
                  Muestra solo pedidos en un estado específico (ej: solo
                  Pendientes).
                </span>
              </div>
            </div>
            <div className="flex gap-3 items-start" data-id="element-1371">
              <Calendar className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-1372" />
              <div data-id="element-1373">
                <strong className="block text-sm text-gray-900" data-id="element-1374">
                  Filtro por Fecha
                </strong>
                <span className="text-sm text-gray-500" data-id="element-1375">
                  Navega entre días. Por defecto muestra el día actual.
                </span>
              </div>
            </div>
            <div className="flex gap-3 items-start" data-id="element-1376">
              <Clock className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-1377" />
              <div data-id="element-1378">
                <strong className="block text-sm text-gray-900" data-id="element-1379">
                  Hora de Entrega
                </strong>
                <span className="text-sm text-gray-500" data-id="element-1380">
                  Organiza los pedidos por turno de entrega.
                </span>
              </div>
            </div>
          </div>
        </Section>

        <Section title="Impresión de Comandas" id="orders-print" data-id="element-1381">
          <p className="mb-4" data-id="element-1382">
            Cada pedido puede imprimirse como comanda individual para cocina.
          </p>
          <StepList steps={['Haz clic en el ícono de impresora en la fila del pedido', 'Se abrirá una vista previa de la comanda', 'Confirma la impresión desde el diálogo del navegador']} data-id="element-1383" />
          <p className="text-sm text-gray-500 mt-2" data-id="element-1384">
            La comanda incluye: nombre del empleado, empresa, plato, opciones
            seleccionadas, notas especiales y hora de entrega.
          </p>
        </Section>

        <FAQ items={[{
        q: '¿Qué pasa si un empleado cancela su pedido?',
        a: 'El pedido aparecerá como Cancelado automáticamente en la lista. No necesitas hacer nada adicional, pero el sistema registrará la cancelación.'
      }, {
        q: '¿Puedo editar un pedido después de recibido?',
        a: 'No es posible editar el contenido del pedido para mantener la integridad de los datos. Si hay un error, cancela el pedido y pide al empleado que lo vuelva a hacer.'
      }, {
        q: '¿Los pedidos de días anteriores se pueden ver?',
        a: 'Sí, usa el filtro de fecha en la parte superior para navegar al día que necesitas consultar.'
      }]} data-id="element-1385" />
      </div>
    </div>;
  const renderLabelsContent = () => <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-1386">
      <div className="prose prose-gray max-w-none" data-id="element-1387">
        <Section title="¿Qué son las Etiquetas?" id="labels-overview" data-id="element-1388">
          <p data-id="element-1389">
            Las etiquetas son stickers o papeles identificadores que se colocan
            en cada pedido antes de la entrega. Permiten que el repartidor y el
            empleado identifiquen rápidamente su pedido sin abrir el empaque.
          </p>
        </Section>

        <Section title="Contenido de Cada Etiqueta" id="labels-content" data-id="element-1390">
          <div className="bg-white border-2 border-gray-800 rounded-lg p-4 max-w-xs mx-auto shadow-sm my-6 font-mono text-sm" data-id="element-1391">
            <div className="text-center border-b border-gray-200 pb-2 mb-2" data-id="element-1392">
              <h4 className="font-bold text-lg" data-id="element-1393">EMPRESA EJEMPLO</h4>
              <p className="text-xs text-gray-500" data-id="element-1394">12:30 PM</p>
            </div>
            <div className="mb-3" data-id="element-1395">
              <p className="font-bold text-base" data-id="element-1396">JUAN PÉREZ</p>
              <p className="text-xs text-gray-500" data-id="element-1397">#12345</p>
            </div>
            <div className="mb-3 border-b border-gray-200 pb-2" data-id="element-1398">
              <p className="font-bold" data-id="element-1399">Pollo al Horno</p>
              <ul className="text-xs list-disc pl-4 text-gray-600 mt-1" data-id="element-1400">
                <li data-id="element-1401">Arroz Blanco</li>
                <li data-id="element-1402">Habichuelas Rojas</li>
                <li data-id="element-1403">Ensalada Verde</li>
              </ul>
            </div>
            <div className="flex justify-between items-end" data-id="element-1404">
              <p className="text-xs text-gray-400" data-id="element-1405">Cricket</p>
              <p className="font-bold text-lg" data-id="element-1406">RD$ 250.00</p>
            </div>
          </div>
        </Section>

        <Section title="Paso a Paso: Generar Etiquetas" id="labels-steps" data-id="element-1407">
          <StepList steps={["Accede a la sección 'Etiquetas' desde el menú lateral izquierdo", 'Selecciona la fecha de entrega (por defecto: hoy)', 'Opcionalmente, filtra por empresa para imprimir por lotes', 'Revisa la lista de pedidos que aparecerá en pantalla', "Haz clic en 'Imprimir Etiquetas' o 'Imprimir Todo'", 'En el diálogo de impresión del navegador, selecciona tu impresora', 'Configura el tamaño de papel según tus etiquetas adhesivas']} data-id="element-1408" />
        </Section>

        <Section title="Configuración de Impresión" id="labels-config" data-id="element-1409">
          <div className="grid sm:grid-cols-2 gap-4" data-id="element-1410">
            <div className="p-4 bg-gray-50 rounded-lg" data-id="element-1411">
              <h4 className="font-semibold mb-2 flex items-center gap-2" data-id="element-1412">
                <Printer size={16} data-id="element-1413" /> Recomendada
              </h4>
              <ul className="text-sm space-y-1 text-gray-600" data-id="element-1414">
                <li data-id="element-1415">• Tamaño de papel: A4 o Letter</li>
                <li data-id="element-1416">• Orientación: Vertical (Portrait)</li>
                <li data-id="element-1417">• Márgenes: Mínimos o ninguno</li>
                <li data-id="element-1418">• Escala: 100% (no ajustar a página)</li>
              </ul>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg" data-id="element-1419">
              <h4 className="font-semibold mb-2 flex items-center gap-2" data-id="element-1420">
                <AlertCircle size={16} data-id="element-1421" /> Importante
              </h4>
              <p className="text-sm text-gray-600" data-id="element-1422">
                Desactiva los encabezados y pies de página del navegador en la
                configuración de impresión para obtener un resultado limpio.
              </p>
            </div>
          </div>
        </Section>

        <Section title="Mejores Prácticas" id="labels-best-practices" data-id="element-1423">
          <div className="grid gap-2" data-id="element-1424">
            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 p-2 rounded" data-id="element-1425">
              <CheckCircle2 size={16} data-id="element-1426" /> Imprime las etiquetas ANTES de comenzar
              la preparación
            </div>
            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 p-2 rounded" data-id="element-1427">
              <CheckCircle2 size={16} data-id="element-1428" /> Pega la etiqueta en la parte superior
              del empaque, visible
            </div>
            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 p-2 rounded" data-id="element-1429">
              <CheckCircle2 size={16} data-id="element-1430" /> Verifica que el nombre del empleado sea
              legible
            </div>
            <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 p-2 rounded" data-id="element-1431">
              <X size={16} data-id="element-1432" /> No imprimas etiquetas de días anteriores para el
              día actual
            </div>
          </div>
        </Section>
      </div>
    </div>;
  const renderMenuContent = () => <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-1433">
      <div className="prose prose-gray max-w-none" data-id="element-1434">
        <Section title="Estructura del Menú" id="menu-structure" data-id="element-1435">
          <p data-id="element-1436">
            El menú está organizado en Categorías que contienen Platos. Cada
            plato puede tener Secciones de Opciones (como tipo de arroz,
            proteína, ensalada) que el empleado debe o puede seleccionar al
            pedir.
          </p>
          <div className="flex items-center gap-2 my-4 text-sm font-medium text-gray-600 bg-gray-50 p-3 rounded-lg justify-center" data-id="element-1437">
            Categoría <ChevronRight size={14} data-id="element-1438" /> Platos{' '}
            <ChevronRight size={14} data-id="element-1439" /> Secciones de Opciones{' '}
            <ChevronRight size={14} data-id="element-1440" /> Opciones
          </div>
        </Section>

        <Section title="Crear un Nuevo Plato" id="menu-create" data-id="element-1441">
          <div className="grid md:grid-cols-2 gap-6" data-id="element-1442">
            <div data-id="element-1443">
              <h4 className="font-semibold mb-3" data-id="element-1444">Campos Requeridos</h4>
              <ul className="space-y-2 text-sm text-gray-600" data-id="element-1445">
                <li className="flex gap-2" data-id="element-1446">
                  <span className="font-bold min-w-[80px]" data-id="element-1447">Nombre:</span>
                  Descriptivo y apetitoso (ej: 'Pollo al Horno')
                </li>
                <li className="flex gap-2" data-id="element-1448">
                  <span className="font-bold min-w-[80px]" data-id="element-1449">Precio:</span>
                  En pesos dominicanos, INCLUYENDO ITBIS (18%)
                </li>
                <li className="flex gap-2" data-id="element-1450">
                  <span className="font-bold min-w-[80px]" data-id="element-1451">Categoría:</span>A
                  qué sección pertenece
                </li>
                <li className="flex gap-2" data-id="element-1452">
                  <span className="font-bold min-w-[80px]" data-id="element-1453">Disponible:</span>
                  Si está activo para pedidos hoy
                </li>
              </ul>
            </div>
            <div data-id="element-1454">
              <h4 className="font-semibold mb-3" data-id="element-1455">Recomendados</h4>
              <ul className="space-y-2 text-sm text-gray-600" data-id="element-1456">
                <li className="flex gap-2" data-id="element-1457">
                  <span className="font-bold min-w-[80px]" data-id="element-1458">Descripción:</span>
                  Ingredientes principales, método de cocción
                </li>
                <li className="flex gap-2" data-id="element-1459">
                  <span className="font-bold min-w-[80px]" data-id="element-1460">Imagen:</span>
                  Foto real del plato (aumenta ventas)
                </li>
              </ul>
            </div>
          </div>
        </Section>

        <Section title="Secciones de Opciones" id="menu-options" data-id="element-1461">
          <Callout type="info" title="Fundamental" data-id="element-1462">
            Las secciones de opciones permiten que el empleado personalice su
            pedido. Son fundamentales para el Plato del Día donde hay múltiples
            guarniciones.
          </Callout>

          <div className="space-y-4 mt-4" data-id="element-1463">
            <div className="border border-gray-200 rounded-lg p-4" data-id="element-1464">
              <h4 className="font-bold text-gray-900 mb-2" data-id="element-1465">Tipos de Sección</h4>
              <div className="grid sm:grid-cols-2 gap-4" data-id="element-1466">
                <div data-id="element-1467">
                  <span className="text-xs font-bold uppercase text-red-600 bg-red-50 px-2 py-0.5 rounded" data-id="element-1468">
                    Obligatoria
                  </span>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-1469">
                    El empleado DEBE seleccionar una opción. Ej: 'Tipo de
                    Arroz'.
                  </p>
                </div>
                <div data-id="element-1470">
                  <span className="text-xs font-bold uppercase text-blue-600 bg-blue-50 px-2 py-0.5 rounded" data-id="element-1471">
                    Opcional
                  </span>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-1472">
                    El empleado puede o no seleccionar. Ej: 'Salsa adicional'.
                  </p>
                </div>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4" data-id="element-1473">
              <h4 className="font-bold text-gray-900 mb-2" data-id="element-1474">Configuración</h4>
              <StepList steps={["Abre el plato y ve a 'Opciones del Plato'", "Haz clic en 'Agregar Sección'", "Escribe el nombre (ej: 'Carbohidrato')", 'Marca si es Obligatoria u Opcional', 'Define cuántas opciones puede seleccionar (mínimo y máximo)', "Agrega las opciones individuales (ej: 'Arroz Blanco', 'Moro')", 'Cada opción puede tener un precio adicional (+RD$)']} data-id="element-1475" />
            </div>
          </div>
        </Section>

        <Section title="Precios e ITBIS" id="menu-prices" data-id="element-1476">
          <p className="mb-4" data-id="element-1477">
            Todos los precios en el sistema incluyen ITBIS (18%). El sistema
            calcula automáticamente el precio sin ITBIS para empleados exentos.
          </p>
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-1478">
            <h4 className="font-semibold mb-2" data-id="element-1479">
              Ejemplo de Cálculo Automático
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-1480">
              <div data-id="element-1481">Precio configurado (con ITBIS):</div>
              <div className="font-mono font-bold" data-id="element-1482">RD$ 295.00</div>
              <div data-id="element-1483">Precio para empleado exento:</div>
              <div className="font-mono font-bold" data-id="element-1484">RD$ 250.00</div>
            </div>
            <p className="text-xs text-gray-500 mt-2 italic" data-id="element-1485">
              * No necesitas crear platos separados para empleados exentos.
            </p>
          </div>
        </Section>

        <FAQ items={[{
        q: '¿Puedo tener el mismo plato en varias categorías?',
        a: 'No, cada plato pertenece a una sola categoría. Si necesitas mostrarlo en varias, crea duplicados.'
      }, {
        q: '¿Qué pasa si cambio el precio de un plato?',
        a: 'Los pedidos ya realizados mantienen el precio original. Solo los nuevos pedidos usarán el precio actualizado.'
      }, {
        q: '¿Puedo eliminar un plato que tiene pedidos activos?',
        a: 'Sí, pero los pedidos existentes no se verán afectados. El plato solo desaparece para nuevos pedidos.'
      }, {
        q: '¿Cuántas opciones puedo agregar a una sección?',
        a: 'No hay límite técnico, pero se recomienda no más de 8-10 opciones por sección para no abrumar al empleado.'
      }]} data-id="element-1486" />
      </div>
    </div>;
  const activeTabInfo = tabs.find(t => t.id === activeTab);
  return <div className="min-h-screen bg-gray-50" data-id="element-1487">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20" data-id="element-1488">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1489">
          <div className="py-6" data-id="element-1490">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1491">Guía de Uso</h1>
            <p className="text-gray-500 mt-1" data-id="element-1492">
              Documentación completa del panel administrativo
            </p>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 overflow-x-auto pb-px no-scrollbar" data-id="element-1493">
            {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`
                    flex items-center gap-2 px-6 py-3 text-sm font-medium rounded-t-lg transition-all relative whitespace-nowrap
                    ${isActive ? 'bg-gray-50 text-gray-900 border-x border-t border-gray-200 -mb-px z-10' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}
                  `} data-id="element-1494">
                  <Icon size={18} className={isActive ? 'text-gray-900' : 'text-gray-400'} data-id="element-1495" />
                  {tab.label}
                  {isActive && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-50" data-id="element-1496" />}
                </button>;
          })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1497">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-1498">
          {/* Sidebar Navigation (Desktop) */}
          <div className="hidden lg:block w-64 shrink-0" data-id="element-1499">
            <div className="sticky top-32 space-y-1" data-id="element-1500">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-3" data-id="element-1501">
                En esta página
              </h3>
              {activeTab === 'orders' && <>
                  <button onClick={() => scrollToSection('orders-overview')} className="block w-full text-left px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-white rounded-md transition-colors" data-id="element-1502">
                    Visión General
                  </button>
                  <button onClick={() => scrollToSection('orders-flow')} className="block w-full text-left px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-white rounded-md transition-colors" data-id="element-1503">
                    Flujo de Estados
                  </button>
                  <button onClick={() => scrollToSection('orders-status')} className="block w-full text-left px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-white rounded-md transition-colors" data-id="element-1504">
                    Cambiar Estado
                  </button>
                  <button onClick={() => scrollToSection('orders-filters')} className="block w-full text-left px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-white rounded-md transition-colors" data-id="element-1505">
                    Filtros
                  </button>
                  <button onClick={() => scrollToSection('orders-print')} className="block w-full text-left px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-white rounded-md transition-colors" data-id="element-1506">
                    Impresión
                  </button>
                </>}
              {activeTab === 'labels' && <>
                  <button onClick={() => scrollToSection('labels-overview')} className="block w-full text-left px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-white rounded-md transition-colors" data-id="element-1507">
                    ¿Qué son?
                  </button>
                  <button onClick={() => scrollToSection('labels-content')} className="block w-full text-left px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-white rounded-md transition-colors" data-id="element-1508">
                    Contenido
                  </button>
                  <button onClick={() => scrollToSection('labels-steps')} className="block w-full text-left px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-white rounded-md transition-colors" data-id="element-1509">
                    Generar Etiquetas
                  </button>
                  <button onClick={() => scrollToSection('labels-config')} className="block w-full text-left px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-white rounded-md transition-colors" data-id="element-1510">
                    Configuración
                  </button>
                  <button onClick={() => scrollToSection('labels-best-practices')} className="block w-full text-left px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-white rounded-md transition-colors" data-id="element-1511">
                    Mejores Prácticas
                  </button>
                </>}
              {activeTab === 'menu' && <>
                  <button onClick={() => scrollToSection('menu-structure')} className="block w-full text-left px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-white rounded-md transition-colors" data-id="element-1512">
                    Estructura
                  </button>
                  <button onClick={() => scrollToSection('menu-create')} className="block w-full text-left px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-white rounded-md transition-colors" data-id="element-1513">
                    Crear Plato
                  </button>
                  <button onClick={() => scrollToSection('menu-options')} className="block w-full text-left px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-white rounded-md transition-colors" data-id="element-1514">
                    Opciones
                  </button>
                  <button onClick={() => scrollToSection('menu-prices')} className="block w-full text-left px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-white rounded-md transition-colors" data-id="element-1515">
                    Precios e ITBIS
                  </button>
                </>}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0" data-id="element-1516">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-10" data-id="element-1517">
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium mb-6 ${activeTabInfo?.color}`} data-id="element-1518">
                {activeTabInfo && <activeTabInfo.icon size={16} data-id="element-1519" />}
                {activeTabInfo?.label}
              </div>

              {activeTab === 'orders' && renderOrdersContent()}
              {activeTab === 'labels' && renderLabelsContent()}
              {activeTab === 'menu' && renderMenuContent()}
            </div>
          </div>
        </div>
      </div>
    </div>;
}